import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect, useRef } from 'react';
import { dashboardsService, Dashboard, DashboardListItem, DashboardFilter, Chart, ApiClientError, ChartSuggestionsData } from '../api';
import type { DashboardSettings, UpdateDashboardSettingsRequest } from '../api/types';
import { invalidateDashboard as invalidateDashboardCache } from '../components/charts/hooks';
import { useAuth } from './AuthContext';

// ─── Internal config type (converts API date strings → Date objects) ─────────

interface DashboardConfig {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  datasetViewId?: string;
  createdAt: Date;
  updatedAt: Date;
  jobId?: string | null;
  settings?: Partial<DashboardSettings> | null;
  filters?: DashboardFilter[];
  charts?: Chart[];
  chartSuggestions?: ChartSuggestionsData | null;
  /** Data source name (e.g. "CRM Simulator") */
  dataSourceName?: string | null;
  /** Connector ID for icon display (e.g. "salesforce", "simulator") */
  connectorId?: string | null;
  /** Chart count from list API (avoids needing full charts array) */
  chartCount?: number;
  /** AI-generated analysis questions from the dataset view */
  questions?: string[];
  /** Dashboard thumbnail URL */
  thumbnailUrl?: string | null;
}

type AppView = 'dashboard' | 'datasources' | 'creating';

// ─── Context type ────────────────────────────────────────────────────────────

interface DashboardContextType {
  dashboards: DashboardConfig[];
  currentDashboard: DashboardConfig | null;
  currentDashboardDetails: DashboardConfig | null;
  currentView: AppView;
  isLoading: boolean;
  isLoadingDetails: boolean;
  error: string | null;
  createDashboardWithView: (datasetViewId: string) => Promise<DashboardConfig | null>;
  deleteDashboard: (id: string) => Promise<string | null | false>;
  updateDashboard: (id: string, updates: Partial<DashboardConfig>) => Promise<boolean>;
  setCurrentDashboard: (id: string) => Promise<boolean>;
  setCurrentView: (view: AppView) => void;
  duplicateDashboard: (id: string) => Promise<DashboardConfig | null>;
  refreshDashboards: () => Promise<void>;
  loadDashboardDetails: (id: string) => Promise<void>;
  /** Patch a single chart inside the cached currentDashboardDetails (avoids full re-fetch). */
  updateChartInDetails: (chartId: string, updatedChart: Chart) => void;
  /** Append a new chart to the cached currentDashboardDetails (Add Chart flow). */
  addChartToDetails: (newChart: Chart) => void;
  /** Patch chart positions in the cached currentDashboardDetails (layout drag/resize). */
  updateLayoutInDetails: (items: { id: string; x: number; y: number; w: number; h: number }[]) => void;
  /** Update a dashboard's metadata in the sidebar list (e.g. from plan preview, cancel, regenerate). */
  updateDashboardInList: (id: string, updates: Partial<Pick<DashboardConfig, 'name' | 'description' | 'status' | 'jobId'>>) => void;
  /** Optimistic patch + PATCH API for dashboard settings (debounced by caller). */
  updateDashboardSettings: (id: string, partial: UpdateDashboardSettingsRequest) => Promise<void>;
  clearError: () => void;
  /** Chart ID to scroll to after navigation (e.g. new chart created). One-shot, cleared after use. */
  scrollToChartId: string | null;
  setScrollToChartId: (id: string | null) => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);
DashboardContext.displayName = 'DashboardContext';

export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

// ─── Converters (pure, outside component) ────────────────────────────────────

const convertDashboardDetail = (dashboard: Dashboard): DashboardConfig => ({
  id: dashboard.id,
  name: dashboard.name,
  description: dashboard.description,
  status: dashboard.status,
  datasetViewId: dashboard.datasetViewId,
  createdAt: new Date(dashboard.createdAt),
  updatedAt: new Date(dashboard.updatedAt),
  jobId: dashboard.jobId,
  settings: dashboard.settings ?? null,
  filters: dashboard.filters,
  charts: dashboard.charts,
  chartSuggestions: dashboard.chartSuggestions ?? null,
  thumbnailUrl: (dashboard as any).thumbnailUrl ?? null,
});

const convertDashboardListItem = (item: DashboardListItem): DashboardConfig => ({
  id: item.id,
  name: item.name,
  description: item.description,
  status: item.status,
  createdAt: new Date(item.createdAt),
  updatedAt: new Date(item.updatedAt),
  jobId: item.jobId,
  dataSourceName: item.dataSourceName ?? null,
  connectorId: item.connectorId ?? null,
  chartCount: item.chartCount ?? 0,
  questions: item.questions ?? [],
  thumbnailUrl: (item as any).thumbnailUrl ?? null,
});

// ─── Helper to extract error message ─────────────────────────────────────────

const extractError = (err: unknown, fallback: string): string =>
  err instanceof ApiClientError ? err.error : fallback;

// ─── Provider ────────────────────────────────────────────────────────────────

interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [dashboards, setDashboards] = useState<DashboardConfig[]>([]);
  const [currentDashboard, setCurrentDashboardState] = useState<DashboardConfig | null>(null);
  const [currentDashboardDetails, setCurrentDashboardDetails] = useState<DashboardConfig | null>(null);
  const [currentView, setCurrentView] = useState<AppView>('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track latest detail-load request to avoid race conditions
  const detailsRequestId = useRef(0);

  // ── refreshDashboards ────────────────────────────────────────────────────

  const refreshDashboards = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await dashboardsService.getUserDashboards(1, 100);
      setDashboards(response.dashboards.map(convertDashboardListItem));
    } catch (err) {
      setError(extractError(err, 'Failed to load dashboards'));
      console.error('Failed to fetch dashboards:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load dashboards when authenticated (and re-fetch on login)
  useEffect(() => {
    if (isAuthenticated) {
      refreshDashboards();
    } else {
      setDashboards([]);
    }
  }, [isAuthenticated, refreshDashboards]);

  // Note: currentDashboard is set by DashboardViewPage based on URL params.
  // No auto-select here — the URL is the source of truth.

  // ── loadDashboardDetails (with race-condition guard) ─────────────────────

  // Use a ref to avoid adding currentDashboardDetails to deps (which would
  // cause an infinite loop: load → set details → deps change → load again).
  const currentDetailsIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentDetailsIdRef.current = currentDashboardDetails?.id ?? null;
  }, [currentDashboardDetails?.id]);

  const loadDashboardDetails = useCallback(async (id: string): Promise<void> => {
    const requestId = ++detailsRequestId.current;

    // Only show loading spinner on initial load — if we already have this
    // dashboard's details, keep them visible during the background refresh.
    const isRefresh = currentDetailsIdRef.current === id;

    try {
      if (!isRefresh) {
        setIsLoadingDetails(true);
        setCurrentDashboardDetails(null);
      }
      setError(null);
      const dashboard = await dashboardsService.getDashboard(id);

      // Only apply if this is still the latest request
      if (requestId !== detailsRequestId.current) return;

      setCurrentDashboardDetails(convertDashboardDetail(dashboard));
    } catch (err) {
      if (requestId !== detailsRequestId.current) return;
      setError(extractError(err, 'Failed to load dashboard details'));
      console.error('Failed to load dashboard details:', err);
    } finally {
      if (requestId === detailsRequestId.current) {
        setIsLoadingDetails(false);
      }
    }
  }, []);

  // Load details whenever currentDashboard changes
  useEffect(() => {
    if (currentDashboard?.id) {
      loadDashboardDetails(currentDashboard.id);
    } else {
      setCurrentDashboardDetails(null);
    }
  }, [currentDashboard?.id, loadDashboardDetails]);

  // ── createDashboardWithView ──────────────────────────────────────────────

  const createDashboardWithView = useCallback(async (datasetViewId: string): Promise<DashboardConfig | null> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await dashboardsService.createDashboard({ datasetViewId });

      const config: DashboardConfig = {
        id: response.dashboardId,
        name: 'New Dashboard',
        description: undefined,
        status: 'generating',
        createdAt: new Date(),
        updatedAt: new Date(),
        jobId: response.jobId,
      };

      // Add the new dashboard to the list so it's visible immediately
      setDashboards(prev => [config, ...prev]);

      return config;
    } catch (err) {
      setError(extractError(err, 'Failed to create dashboard with AI'));
      console.error('Failed to create dashboard with view:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── deleteDashboard ──────────────────────────────────────────────────────

  const deleteDashboard = useCallback(async (id: string): Promise<string | null | false> => {
    const dashboard = dashboards.find(d => d.id === id);
    if (!dashboard) {
      setError('Dashboard not found');
      return false;
    }

    try {
      setError(null);
      await dashboardsService.deleteDashboard(id);

      // Clean up in-memory chart/filter data cache for the deleted dashboard
      invalidateDashboardCache(id);

      const remaining = dashboards.filter(d => d.id !== id);
      setDashboards(remaining);

      // Clear stale detail state so it doesn't flash when navigating
      // to another dashboard after deletion.
      setCurrentDashboardState(null);
      setCurrentDashboardDetails(null);

      const nextDashboard = remaining[0] || null;

      // Return next dashboard ID or null (no dashboards left)
      // Navigation is handled by the caller (Sidebar) — no need to set currentDashboard here.
      return nextDashboard?.id ?? null;
    } catch (err) {
      setError(extractError(err, 'Failed to delete dashboard'));
      console.error('Failed to delete dashboard:', err);
      return false;
    }
  }, [dashboards]);

  // ── updateDashboard ──────────────────────────────────────────────────────

  const updateDashboard = useCallback(async (id: string, updates: Partial<DashboardConfig>): Promise<boolean> => {
    if (!dashboards.some(d => d.id === id)) {
      setError('Dashboard not found');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      const updateData: Record<string, unknown> = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;

      const updatedDashboard = await dashboardsService.updateDashboard(id, updateData);
      const config = convertDashboardDetail(updatedDashboard);

      setDashboards(prev => prev.map(d => d.id === id ? config : d));

      if (currentDashboard?.id === id) {
        setCurrentDashboardState(config);
      }

      // Also patch currentDashboardDetails so the active dashboard view reflects changes
      setCurrentDashboardDetails(prev => {
        if (!prev || prev.id !== id) return prev;
        return { ...prev, name: config.name, description: config.description };
      });

      return true;
    } catch (err) {
      setError(extractError(err, 'Failed to update dashboard'));
      console.error('Failed to update dashboard:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dashboards, currentDashboard?.id]);

  // ── setCurrentDashboard ──────────────────────────────────────────────────

  const setCurrentDashboard = useCallback(async (id: string): Promise<boolean> => {
    const dashboard = dashboards.find(d => d.id === id);

    // Set a minimal placeholder if not in the list yet — the auto-load
    // effect on currentDashboard.id will fetch full details.
    setCurrentDashboardState(
      dashboard ?? { id, name: '', createdAt: new Date(), updatedAt: new Date() }
    );
    return true;
  }, [dashboards]);

  // ── duplicateDashboard ───────────────────────────────────────────────────

  const duplicateDashboard = useCallback(async (id: string): Promise<DashboardConfig | null> => {
    if (!dashboards.some(d => d.id === id)) {
      setError('Dashboard not found');
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);

      const duplicated = await dashboardsService.duplicateDashboard(id);
      const config = convertDashboardDetail(duplicated);
      setDashboards(prev => [...prev, config]);
      return config;
    } catch (err) {
      setError(extractError(err, 'Failed to duplicate dashboard'));
      console.error('Failed to duplicate dashboard:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [dashboards]);

  // ── updateChartInDetails ─────────────────────────────────────────────────

  const updateChartInDetails = useCallback((chartId: string, updatedChart: Chart) => {
    setCurrentDashboardDetails(prev => {
      if (!prev?.charts) return prev;
      return {
        ...prev,
        charts: prev.charts.map(c => c.id === chartId ? updatedChart : c),
      };
    });
  }, []);

  // ── addChartToDetails ────────────────────────────────────────────────────

  const addChartToDetails = useCallback((newChart: Chart) => {
    setCurrentDashboardDetails(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        charts: [...(prev.charts || []), newChart],
      };
    });
  }, []);

  // ── updateLayoutInDetails ────────────────────────────────────────────────
  // Patch chart positionConfig in the cached details so that navigating
  // away and back renders the correct layout without an API round-trip.

  const updateLayoutInDetails = useCallback((items: { id: string; x: number; y: number; w: number; h: number }[]) => {
    setCurrentDashboardDetails(prev => {
      if (!prev?.charts) return prev;
      const posMap = new Map(items.map(i => [i.id, { x: i.x, y: i.y, w: i.w, h: i.h }]));
      return {
        ...prev,
        charts: prev.charts.map(c => {
          const pos = posMap.get(c.id);
          return pos ? { ...c, positionConfig: pos } : c;
        }),
      };
    });
  }, []);

  // ── updateDashboardInList ────────────────────────────────────────────────
  // Lightweight update for sidebar/list — only patches local state,
  // no API call.  Used during generation to reflect the LLM-chosen name.

  const updateDashboardInList = useCallback((id: string, updates: Partial<Pick<DashboardConfig, 'name' | 'description' | 'status' | 'jobId'>>) => {
    setDashboards(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
    setCurrentDashboardState(prev => prev && prev.id === id ? { ...prev, ...updates } : prev);
    // Also patch currentDashboardDetails so settings panel and other detail consumers stay in sync
    setCurrentDashboardDetails(prev => prev && prev.id === id ? { ...prev, ...updates } : prev);
  }, []);

  // ── updateDashboardSettings (optimistic + API) ──────────────────────────

  const updateDashboardSettings = useCallback(async (id: string, partial: UpdateDashboardSettingsRequest): Promise<void> => {
    // Optimistic update
    setCurrentDashboardDetails(prev => {
      if (!prev || prev.id !== id) return prev;
      const existing = (prev.settings ?? {}) as Record<string, unknown>;
      const merged: Record<string, unknown> = { ...existing };
      for (const [key, value] of Object.entries(partial)) {
        if (key === 'layout' && typeof value === 'object' && value !== null && typeof existing.layout === 'object' && existing.layout !== null) {
          merged.layout = { ...(existing.layout as Record<string, unknown>), ...(value as Record<string, unknown>) };
        } else {
          merged[key] = value;
        }
      }
      return { ...prev, settings: merged as Partial<DashboardSettings> };
    });

    // Fire API call (non-blocking)
    try {
      await dashboardsService.updateDashboardSettings(id, partial);
    } catch (err) {
      console.error('Failed to save dashboard settings:', err);
    }
  }, []);

  // ── clearError ───────────────────────────────────────────────────────────

  const clearError = useCallback(() => setError(null), []);

  // ── Scroll-to-chart signal (one-shot, for new chart navigation) ────────
  const [scrollToChartId, setScrollToChartId] = useState<string | null>(null);

  // ── Memoized value ───────────────────────────────────────────────────────

  const value = useMemo<DashboardContextType>(() => ({
    dashboards,
    currentDashboard,
    currentDashboardDetails,
    currentView,
    isLoading,
    isLoadingDetails,
    error,
    createDashboardWithView,
    deleteDashboard,
    updateDashboard,
    setCurrentDashboard,
    setCurrentView,
    duplicateDashboard,
    refreshDashboards,
    loadDashboardDetails,
    updateChartInDetails,
    addChartToDetails,
    updateLayoutInDetails,
    updateDashboardInList,
    updateDashboardSettings,
    clearError,
    scrollToChartId,
    setScrollToChartId,
  }), [
    dashboards,
    currentDashboard,
    currentDashboardDetails,
    currentView,
    isLoading,
    isLoadingDetails,
    error,
    createDashboardWithView,
    deleteDashboard,
    updateDashboard,
    setCurrentDashboard,
    setCurrentView,
    duplicateDashboard,
    refreshDashboards,
    loadDashboardDetails,
    updateChartInDetails,
    addChartToDetails,
    updateLayoutInDetails,
    updateDashboardInList,
    updateDashboardSettings,
    clearError,
    scrollToChartId,
    setScrollToChartId,
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};